import { useState } from 'react';
import { uploadMedia } from '../utils/mediaUploadUtils';
import { CognitoAuthService } from '../../../services/cognitoService';
import { debug } from "../../../utils/debugLogger";

interface UseMediaUploadProps {
  onUploadComplete?: (cloudFrontUrl: string, s3Key: string) => void;
  onError?: (error: string) => void;
}

export const useMediaUpload = (props?: UseMediaUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  
  const upload = async ({
    file,
    customerId,
    isLogo = false,
    mediaType = 'processed',
    tags = [],
    additionalMetadata = {},
    idToken
  }: {
    file: File;
    customerId: string;
    isLogo?: boolean;
    mediaType?: 'processed' | 'raw';
    tags?: string[];
    additionalMetadata?: Record<string, any>;
    idToken: string;
  }) => {
    try {
      setIsUploading(true);
      setUploadError(null);
      setUploadProgress(10);
      
      const userEmail =
        CognitoAuthService.getUserEmail(idToken) ||
        CognitoAuthService.getUserId(idToken) ||
        'user';
      
      setUploadProgress(30);
      
      // Use the centralized upload function
      const result = await uploadMedia({
        file,
        customerId,
        userEmail,
        token: idToken,
        isLogo,
        mediaType,
        additionalMetadata,
        tags
      });
      
      setUploadProgress(100);
      
      if (!result.success) {
        throw new Error(result.errorMessage || 'Upload failed');
      }
      
      setUploadedUrl(result.cloudFrontUrl);
      
      // Call the onUploadComplete callback if provided
      if (props?.onUploadComplete) {
        props.onUploadComplete(result.cloudFrontUrl, result.s3Key);
      }
      
      return result;
    } catch (err) {
      debug.error('Error in useMediaUpload:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setUploadError(errorMessage);
      
      // Call the onError callback if provided
      if (props?.onError) {
        props.onError(errorMessage);
      }
      
      return {
        success: false,
        cloudFrontUrl: '',
        s3Key: '',
        errorMessage
      };
    } finally {
      setIsUploading(false);
    }
  };
  
  return {
    upload,
    isUploading,
    uploadProgress,
    uploadError,
    uploadedUrl,
    resetUpload: () => {
      setUploadError(null);
      setUploadedUrl(null);
      setUploadProgress(0);
    }
  };
};